import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DeliveryInfo, PromoCode, UserData } from '../../@types/order.types';
import {
  addOrder,
  createMonopayOrder,
  createAcquiringOrder,
  getDeliveryCities,
  getDeliveryWarehouses,
  addPromoCode,
} from './orderOperations';

type InitialState = {
  userData: UserData;
  delivery: DeliveryInfo;
  promoCode: string;
  promoCodeDiscount: number;
  discountValue: number;
  together: number;
  isChangedProductInCart: boolean;
  orderNum: string;
  isLoading: boolean;
  isLoadingCities: boolean;
  isLoadingWarehouses: boolean;
  isLoadingPromoCode: boolean;
  error: string | null;
  errorPromoCode: string | null;
};

const defaultUserData = {
  tel: '',
  text: '',
};

const deliveryInfo = {
  deliveryType: '',
  cities: [],
  warehouses: [],
  payment: '',
  city: '',
  warehouse: '',
};

const initialState: InitialState = {
  userData: { ...defaultUserData },
  delivery: deliveryInfo,
  promoCode: '',
  promoCodeDiscount: 0,
  discountValue: 0,
  together: 0,
  isChangedProductInCart: false,
  orderNum: '',
  isLoading: false,
  isLoadingCities: false,
  isLoadingWarehouses: false,
  isLoadingPromoCode: false,
  error: null,
  errorPromoCode: null,
};

export const handlePending = (state: InitialState) => {
  state.isLoading = true;
};

export const handlePendingCities = (state: InitialState) => {
  state.isLoadingCities = true;
};

export const handlePendingWarehouses = (state: InitialState) => {
  state.isLoadingWarehouses = true;
};

export const handlePendingAddPromoCode = (state: InitialState) => {
  state.isLoadingPromoCode = true;
};

export const handleFulfilledAddOrder = (state: InitialState, { payload }: PayloadAction<{ orderNum: string }>) => {
  state.userData = { ...defaultUserData };
  state.delivery = deliveryInfo;
  state.promoCode = '';
  state.promoCodeDiscount = 0;
  state.discountValue = 0;
  state.together = 0;
  state.isChangedProductInCart = false;
  state.orderNum = payload.orderNum;
  state.isLoading = false;
  state.error = null;
};

export const handleFulfilledCreateMonopayOrder = (
  state: InitialState,
  { payload }: PayloadAction<{ orderNum: string; status: string }>
) => {
  state.userData = { ...defaultUserData };
  state.delivery = deliveryInfo;
  state.promoCode = '';
  state.promoCodeDiscount = 0;
  state.discountValue = 0;
  state.together = 0;
  state.isChangedProductInCart = false;
  state.orderNum = payload.orderNum;
  state.isLoading = false;
  state.error = null;
};

// The acquiring flow deliberately diverges from its two siblings above on
// the last two fields, because the shopper is being sent to monobank's payment
// page rather than staying on the checkout:
//   * orderNum is left empty — Checkout.tsx opens the "order accepted" modal
//     from a `useEffect` on orderNum, and setting it here would flash that
//     modal over the page in the instant before the browser navigates away.
//   * isLoading stays true — it holds the full-screen backdrop up through the
//     redirect, covering the empty-cart state that clearBasket() renders for a
//     frame. Nothing in this slice is persisted (see store.ts), so a return
//     trip from the bank starts from a fresh initialState and can't get stuck.
export const handleFulfilledCreateAcquiringOrder = (state: InitialState) => {
  state.userData = { ...defaultUserData };
  state.delivery = deliveryInfo;
  state.promoCode = '';
  state.promoCodeDiscount = 0;
  state.discountValue = 0;
  state.together = 0;
  state.isChangedProductInCart = false;
  state.error = null;
};

export const handleRejected = (
  state: InitialState,
  action: PayloadAction<string | undefined> & { meta?: { aborted?: boolean } }
) => {
  // A request cancelled in favor of a newer one (see the abort-on-refire
  // pattern in Delivery.tsx) isn't a real failure — ignore it so a stale
  // city/warehouse search doesn't flash an error over the current one.
  if (action.meta?.aborted) return;
  state.isLoading = false;
  state.error = action.payload ?? 'Unknown error';
};

export const handleRejectedCities = (
  state: InitialState,
  action: PayloadAction<string | undefined> & { meta?: { aborted?: boolean } }
) => {
  if (action.meta?.aborted) return;
  state.isLoadingCities = false;
  state.error = action.payload ?? 'Unknown error';
};

export const handleRejectedWarehouses = (
  state: InitialState,
  action: PayloadAction<string | undefined> & { meta?: { aborted?: boolean } }
) => {
  if (action.meta?.aborted) return;
  state.isLoadingWarehouses = false;
  state.error = action.payload ?? 'Unknown error';
};

export const handleRejectedPromoCode = (
  state: InitialState,
  { payload }: PayloadAction<string | undefined>
) => {
  state.isLoadingPromoCode = false;
  state.errorPromoCode = payload ?? 'Unknown error';
};

export const handleFulfilledGetCities = (state: InitialState, { payload }: PayloadAction<{ cities: string[]}>) => {
  state.delivery.cities = payload.cities;
  state.isLoadingCities = false;
  state.error = null;
};

export const handleFulfilledGetWarehouses = (state: InitialState, { payload }: PayloadAction<{ werehouses: string[]}>) => {
  state.delivery.warehouses = payload.werehouses;
  state.isLoadingWarehouses = false;
  state.error = null;
};

export const handleFulfilledAddPromoCode = (
  state: InitialState,
  { payload }: PayloadAction<{ promoCode: PromoCode }>
) => {
  state.promoCode = payload.promoCode.name;
  state.promoCodeDiscount = payload.promoCode.discount;
  state.isLoadingPromoCode = false;
  state.errorPromoCode = null;
};

const orderSlice = createSlice({
  name: 'order',
  initialState,
  reducers: {
    changeUserTel(state, { payload }: PayloadAction<string>) {
      state.userData.tel = payload;
    },
    changeUserComment(state, { payload }: PayloadAction<string>) {
      state.userData.text = payload;
    },
    checkChangeProductInCart(state, { payload }: PayloadAction<boolean>) {
      state.isChangedProductInCart = payload;
    },
    changeOrderNum(state, { payload }: PayloadAction<string>) {
      state.orderNum = payload;
    },
    changeCity(state, { payload }: PayloadAction<string>) {
      state.delivery.city = payload;
    },
    changeWarehouse(state, { payload }: PayloadAction<string>) {
      state.delivery.warehouse = payload;
    },
    changeDeliveryType(state, { payload }: PayloadAction<string>) {
      state.delivery.deliveryType = payload;
    },
    changePayment(state, { payload }: PayloadAction<string>) {
      state.delivery.payment = payload;
    },
    changeDiscount(state, { payload }: PayloadAction<number>) {
      state.discountValue = payload;
    },
    changeTogether(state, { payload }: PayloadAction<number>) {
      state.together = payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(addOrder.pending, handlePending)
      .addCase(addOrder.fulfilled, handleFulfilledAddOrder)
      .addCase(addOrder.rejected, handleRejected)
      .addCase(createMonopayOrder.pending, handlePending)
      .addCase(createMonopayOrder.fulfilled, handleFulfilledCreateMonopayOrder)
      .addCase(createMonopayOrder.rejected, handleRejected)
      .addCase(createAcquiringOrder.pending, handlePending)
      .addCase(createAcquiringOrder.fulfilled, handleFulfilledCreateAcquiringOrder)
      .addCase(createAcquiringOrder.rejected, handleRejected)
      .addCase(getDeliveryCities.pending, handlePendingCities)
      .addCase(getDeliveryCities.fulfilled, handleFulfilledGetCities)
      .addCase(getDeliveryCities.rejected, handleRejectedCities)
      .addCase(getDeliveryWarehouses.pending, handlePendingWarehouses)
      .addCase(getDeliveryWarehouses.fulfilled, handleFulfilledGetWarehouses)
      .addCase(getDeliveryWarehouses.rejected, handleRejectedWarehouses)
      .addCase(addPromoCode.pending, handlePendingAddPromoCode)
      .addCase(addPromoCode.fulfilled, handleFulfilledAddPromoCode)
      .addCase(addPromoCode.rejected, handleRejectedPromoCode);
  },
});

export const {
  changeUserTel,
  changeUserComment,
  checkChangeProductInCart,
  changeOrderNum,
  changeCity,
  changeWarehouse,
  changeDeliveryType,
  changePayment,
  changeDiscount,
  changeTogether,
} = orderSlice.actions;

export const orderReducer = orderSlice.reducer;
